import Sentry from 'plugins/sentry.TARGET';
import {
  computed,
  ref,
} from '@vue/composition-api';
import {
  Editor,
} from '@tiptap/vue-2';
import { generateHTML } from '@tiptap/html';
import Document from '@tiptap/extension-document';
import Text from '@tiptap/extension-text';
import Paragraph from '@tiptap/extension-paragraph';
import Heading from '@tiptap/extension-heading';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import HardBreak from '@tiptap/extension-hard-break';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import BulletList from '@tiptap/extension-bullet-list';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Gapcursor from '@tiptap/extension-gapcursor';
import Dropcursor from '@tiptap/extension-dropcursor';
import Focus from '@tiptap/extension-focus';
import History from '@tiptap/extension-history';
import Placeholder from '@tiptap/extension-placeholder';

import ImageCompShorttag from 'components/tiptap/ImageCompShorttag';
import StandardLinkShorttag from 'components/tiptap/StandardLinkShorttag';
import ContentLinkListShorttag from 'components/tiptap/ContentLinkListShorttag';
import FaqListShorttag from 'components/tiptap/FaqListShorttag';
import CustomObjectTeaserShorttag from 'components/tiptap/CustomObjectTeaserShorttag';
import LastSeenObjectsShorttag from 'components/tiptap/LastSeenObjectsShorttag';
import RowShorttag from 'components/tiptap/RowShorttag';
import ColShorttag from 'components/tiptap/ColShorttag';
import QuotationBlockShorttag from 'components/tiptap/QuotationBlockShorttag';
import ButtonLinkShorttag from 'components/tiptap/ButtonLinkShorttag';

export default ({
  store,
  route,
  bvToast,
  i18next,
  containerKey,
}) => {
  // convert string from camelCase or PascalCase to kebab-case
  // also remove trailing '-shorttag'
  // this is needed for vue to html-render the component by name, especially on ssr
  const tiptapComponentKebabize = ref((string) => string.replace(/[A-Z]+(?![a-z])|[A-Z]/g, ($, ofs) => (ofs ? '-' : '') + $.toLowerCase()).replace('-shorttag', ''));

  /**
   * there are two kinds of shorttags
   * inlineShorttags:
   *    they can be used inline, inside other tiptap html tags
   *    it is important that they have a custom renderHTML funtion inside their xxxxShorttag.js file
   *    this is needed for SSR of shorttag
   *    i.e. StandardLinkShorttag, colShorttag, ImageCompShorttag,
   * componentShorttags:
   *    they must be used as "standalone" shorttags, without any other html element wrapping them
   *    these componentShorttags always use one of our normal components and are basically just a wrapper for them
   *    it is important for ssr rendering that EVERY componentShorttag has its "normal" matching component
   *     - QuotationBlockShorttag.vue is a wrapper for components/QuotationBlock.vue
   *     - LastSeenObjectsShorttag.vue is a wrapper for components/LastSeenObjects.vue
   *     - ContentLinkListShorttag.vue is a wrapper for components/ContentLinkList.vue
   *     - etc...
   */
  const inlineShorttags = ref({
    StandardLinkShorttag,
    ImageCompShorttag,
  });
  const componentShorttags = ref({
    ButtonLinkShorttag,
    ContentLinkListShorttag,
    FaqListShorttag,
    CustomObjectTeaserShorttag,
    LastSeenObjectsShorttag,
    QuotationBlockShorttag,
  });
  // also insert all custom tiptap (vue-) components-components in here
  // this is needed for ssr
  const componentShorttagsComponents = ref({
    ...StandardLinkShorttag.config.components,
    ...ButtonLinkShorttag.config.components,
    ...ImageCompShorttag.config.components,
    ...ContentLinkListShorttag.config.components,
    ...FaqListShorttag.config.components,
    ...CustomObjectTeaserShorttag.config.components,
    ...LastSeenObjectsShorttag.config.components,
    ...QuotationBlockShorttag.config.components,
  });

  // extensions with value null get custom configurated in tiptapExtensionsConfig()
  const tiptapExtensions = ref({
    Document,
    Text,
    Paragraph: null,
    Heading: null,
    HorizontalRule,
    Bold,
    Italic,
    Strike,
    HardBreak,
    ListItem,
    OrderedList,
    BulletList,
    Link,
    Image,
    Gapcursor,
    Dropcursor,
    Focus,
    History,
    Placeholder,
    RowShorttag,
    ColShorttag,
    ...inlineShorttags.value,
    ...componentShorttags.value,
  });

  // some extensions need some custom configuration
  const tiptapExtensionsConfig = ref(() => {
    const customParagraph = Paragraph.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          class: {
            default: '',
          },
        };
      },
      addCommands() {
        return {
          ...this.parent?.(),
          setAlignmentClassParagraph: (alignment) => ({ commands }) => commands.updateAttributes('paragraph', { class: alignment }),
        };
      },
    });
    tiptapExtensions.value.Paragraph = customParagraph;

    const customHeading = Heading.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          class: {
            default: '',
          },
        };
      },
      addCommands() {
        return {
          ...this.parent?.(),
          setAlignmentClassHeading: (alignment) => ({ commands }) => commands.updateAttributes('heading', { class: alignment }),
        };
      },
    });
    tiptapExtensions.value.Heading = customHeading;
  });

  const currentRoutePath = computed(() => store.getters.getCurrentRoutePath || route.path);

  const tiptapContentStore = computed(() => store.getters.getTiptapContent({
    language: store.getters.getCurrentLanguage,
    page: currentRoutePath.value,
  }));
  const tiptapContentLivePublished = computed(() => Boolean(tiptapContentStore.value[containerKey]?.live?.contentStatus === 'live'));
  const tiptapContentDraftPublished = computed(() => Boolean(tiptapContentStore.value[containerKey]?.draft?.contentStatus === 'draft'));

  // marker for checking for unsaved changes
  const tiptapEditableChanged = ref(false);

  // this generates the ssr content from tiptap json
  // components can be converted directly to html (generateHTML function)
  // or components have to be used as custom components, in template with <component :is="shorttag">
  const tiptapStatic = ref(null);
  const tiptapStaticInit = (contentStatus) => {
    const returnVal = [];
    if (tiptapContentStore.value[containerKey]?.[contentStatus]?.contentContent) {
      // config of extensions has to be done right before initializing editor
      tiptapExtensionsConfig.value();

      const storeValueParsed = JSON.parse(tiptapContentStore.value[containerKey][contentStatus].contentContent);
      storeValueParsed.content.forEach((elem) => {
        // append component info, needed for <component :is="shorttag">
        if (componentShorttags.value[elem.type]) {
          returnVal.push(elem);
        } else {
          // generate plain html for v-dompurify-html
          returnVal.push(generateHTML({
            type: 'doc',
            content: [elem],
          }, Object.values(tiptapExtensions.value)));
        }
      });
    }

    tiptapEditableChanged.value = false;
    return returnVal;
  };

  // this is the tiptap editor instance
  // Can't be used on SSR!
  // only loads after mount when user is logged in and has "cms.edit" permission
  const tiptapEditable = ref(null);
  const tiptapEditableContentStatus = ref('draft');
  const tiptapEditableInit = (contentStatus, overwrite = false) => {
    let storeValueParsed = {};
    let contentUsed = contentStatus;
    if (overwrite) {
      contentUsed = 'live';
    }
    if (tiptapContentStore.value[containerKey]?.[contentUsed]?.contentContent) {
      storeValueParsed = JSON.parse(tiptapContentStore.value[containerKey][contentUsed].contentContent);
    }

    tiptapEditableChanged.value = false;

    // config of extensions has to be done right before initializing editor
    tiptapExtensionsConfig.value();

    tiptapEditableContentStatus.value = contentStatus;

    // return editable editor instance
    return new Editor({
      extensions: Object.values(tiptapExtensions.value),
      editable: true,
      content: storeValueParsed,
      onUpdate() {
        tiptapEditableChanged.value = new Date().getTime();
      },
    });
  };

  // this function adds custom shorttag to tiptap editor instance
  const tiptapContentInsert = ref((contentData) => {
    tiptapEditable.value.commands.insertContent(contentData);
  });

  // this function replaces custom shorttag in tiptap editor instance
  const tiptapContentReplace = ref((contentData) => {
    tiptapEditable.value.chain().deleteNode().insertContent(contentData).run();
  });

  // has to be done this way for now because extensions custom methods with same name overwrite each other...
  const tiptapContentSetAlignmentClass = ref((alignment) => {
    tiptapEditable.value.chain().focus().setAlignmentClassParagraph(alignment).run();
    tiptapEditable.value.chain().focus().setAlignmentClassHeading(alignment).run();
  });

  const addToaster = (title, message, variant) => {
    bvToast.toast(message, {
      title,
      appendToast: true,
      variant,
    });
  };

  // get tiptap content for current mandant, language and page from API
  const tiptapContentGet = ({ previewDraft }) => store.dispatch('TIPTAP_CONTENT_GET', {
    language: store.getters.getCurrentLanguage,
    page: currentRoutePath.value,
    containerKey,
    previewDraft,
  }).catch((error) => {
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error);
    } else {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });

  // send tiptap content for current mandant, language and page to API
  const tiptapContentSet = ({
    language, page, contentStatus, contentAction, containerContent,
  }) => store.dispatch('TIPTAP_CONTENT_SET', {
    language,
    page,
    containerKey,
    contentStatus,
    contentAction,
    containerContent,
  }).then((response) => {
    if (response.success) {
      tiptapEditableChanged.value = false;
      addToaster(i18next.t('tiptap:contentSet.success.title'), i18next.t('tiptap:contentSet.success.body'), 'success');
    } else {
      addToaster(i18next.t('tiptap:contentSet.error.title'), i18next.t('tiptap:contentSet.error.body'), 'danger');
    }
  }).catch((response) => {
    const errorMsg = `${i18next.t('tiptap:contentSet.error.body')}: ${response.errors?.[0]?.text}`;
    addToaster(i18next.t('tiptap:contentSet.error.title'), errorMsg, 'danger');
  });

  // mark current tiptap container as locked to prevent simultaneously editing and overwriting
  const tiptapContentLockToggle = ({
    toggleState,
  }) => store.dispatch('TIPTAP_CONTENT_LOCK_TOGGLE', {
    language: store.getters.getCurrentLanguage,
    page: currentRoutePath.value,
    containerKey,
    toggleState,
  }).catch((response) => {
    const errorMsg = `${i18next.t('tiptap:contentLock.error.body')}: ${response.errors?.[0]?.text}`;
    addToaster(i18next.t('tiptap:contentLock.error.title'), errorMsg, 'danger');
  });

  return {
    componentShorttags,
    componentShorttagsComponents,
    tiptapStatic,
    tiptapStaticInit,
    tiptapEditable,
    tiptapEditableChanged,
    tiptapEditableInit,
    tiptapEditableContentStatus,
    tiptapContentGet,
    tiptapContentSet,
    tiptapContentLockToggle,
    tiptapContentStore,
    tiptapContentLivePublished,
    tiptapContentDraftPublished,
    tiptapComponentKebabize,
    tiptapContentInsert,
    tiptapContentReplace,
    tiptapContentSetAlignmentClass,
  };
};
